import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { type ClassValue, clsx } from "clsx";
import Cookies from "js-cookie";
import { twMerge } from "tailwind-merge";
/**
 * Merges multiple class values into a single string using tailwind-merge and clsx
 * @param {ClassValue[]} inputs - Class values to merge
 * @returns {string} Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a string to title case, replacing underscores with spaces
 * @param {string} str - String to convert
 * @returns {string} Title cased string
 */
export function toTitleCase(str: string) {
  return str
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
}

/**
 * Formats a number with 2 decimal places
 * Returns "NA" for null, undefined, or NaN values
 * @param {number | null | undefined} value - Number to format
 * @returns {string} Formatted number string or "NA" for invalid values
 */
export function formatNumber(
  value: number | null | undefined,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Parses a date string to YYYY-MM-DD format
 * @param {string | undefined} date - Date string to parse
 * @returns {string | null} Formatted date string or null if undefined
 */
export function parseDate(date: string | undefined): string | null {
  return date ? new Date(date).toISOString().split("T")[0] : null;
}

export function coerceUtcIso(input: string): string {
  // already has 'Z' or an offset?
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(input)) return input;
  return input.replace(" ", "T") + "Z";
}

/**
 * Parses a date string to YYYY-MM-DD HH:MM:SS format
 * @param {string | undefined} date - Date string to parse
 * @returns {string | null} Formatted date string or null if undefined
 */
export function parseDateWithHours(date: string | undefined): string | null {
  if (!date) return null;
  const dateObj = new Date(date);
  const dateStr = dateObj.toISOString().split("T")[0];
  const timeStr = dateObj.toTimeString().split(" ")[0];

  return `${dateStr} ${timeStr}`;
}

export function parseDateWithHoursUTC(date: string | undefined): string | null {
  if (!date) return null;
  return parseDateWithHours(coerceUtcIso(date));
}

/**
 * Converts a boolean value to YES/NO string
 * @param {boolean | undefined} value - Boolean to convert
 * @returns {"YES" | "NO" | undefined} YES for true, NO for false, undefined for undefined
 */
export function parseBooleanToYesNo(
  value: boolean | undefined,
): "YES" | "NO" | undefined {
  if (value === undefined) return undefined;
  return value ? "YES" : "NO";
}

/**
 * Handles 401 Unauthorized errors by redirecting to Cognito logout to invalidate session
 */
export function handle401Error(type: "fetch" | "session" = "session"): void {
  // Set URL for redirect after login to "/"  in case of 401
  const currentUrl = "/";
  localStorage.setItem("redirectAfterLogin", currentUrl);

  if (type == "session") {
    // Redirect to Cognito logout to invalidate both local and Cognito sessions
    const logoutUrl = `${import.meta.env.VITE_BACKEND_URL}/sso/cognito/logout`;
    window.location.href = logoutUrl;
  } else {
    // With workspace-start on "/", go directly to SSO redirect
    window.location.href = `${import.meta.env.BASE_URL}auth/redirect`;
  }
}

/**
 * Refreshes the CSRF token by calling the backend endpoint
 * @returns {Promise<boolean>} True if refresh was successful
 */
export async function refreshCSRFToken(): Promise<boolean> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/csrf/refresh`,
      {
        method: "GET",
        credentials: "include",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Checks if a response contains a CSRF error
 * @param {any} responseData - Response data to check
 * @returns {boolean} True if response contains a CSRF error
 */
export function isCSRFError(responseData: any): boolean {
  return (
    responseData?.detail?.includes("CSRF") ||
    responseData?.detail?.includes("csrf") ||
    (typeof responseData === "string" &&
      (responseData.includes("CSRF") || responseData.includes("csrf")))
  );
}

/**
 * Gets the CSRF token from cookies
 * @returns {string | undefined} CSRF token or undefined if not found
 */
export function getCSRFToken(): string | undefined {
  return Cookies.get(`${import.meta.env.VITE_SENTRY_ENV}_csrftoken`);
}

/**
 * Handles a fetch response, redirecting on 401 errors
 * @param {Response} response - Fetch response to handle
 * @returns {Promise<Response>} The original response
 */
export async function handleFetchResponse(
  response: Response,
): Promise<Response> {
  if (response.status === 401) {
    handle401Error("fetch");
  }
  return response;
}

/**
 * Creates a download link for a blob URL and triggers a download
 * @param {string} url - Blob URL to download
 * @param {string} filename - Optional filename to use instead of the one from headers
 */
export function createDownloadLink(url: string, filename?: string): void {
  const link = document.createElement("a");
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Checks if an error is a CSRF error from an Axios response
 * @param {AxiosError} error - The axios error to check
 * @returns {boolean} True if it's a CSRF error
 */
export function hasCSRFError(error: AxiosError): boolean {
  return error.response?.status === 403 && isCSRFError(error.response.data);
}

/**
 * Checks if an error is an authentication error
 * @param {AxiosError} error - The axios error to check
 * @returns {boolean} True if it's an authentication error
 */
export function hasAuthError(error: AxiosError): boolean {
  return error.response?.status === 401;
}

/**
 * Applies a new CSRF token to a request config
 * @param {AxiosRequestConfig} config - Request configuration
 * @param {string} token - CSRF token to apply
 * @returns {AxiosRequestConfig} Updated request configuration
 */
export function applyCSRFToken(
  config: AxiosRequestConfig,
  token: string,
): AxiosRequestConfig {
  if (!config.headers) {
    config.headers = {};
  }
  config.headers["X-CSRF-Token"] = token;
  return config;
}

/**
 * Handles CSRF errors by refreshing token and retrying the request
 * @param {AxiosError} error - The axios error
 * @param {Function} retryRequest - Function to retry the request with new config
 * @returns {Promise<AxiosResponse | null>} The response from the retried request or null
 */
export async function handleCSRFError(
  error: AxiosError,
  retryRequest: (config: AxiosRequestConfig) => Promise<AxiosResponse>,
): Promise<AxiosResponse | null> {
  if (!error.config) return null;

  const refreshSuccessful = await refreshCSRFToken();
  if (!refreshSuccessful) return null;

  const newCSRFToken = getCSRFToken();
  if (!newCSRFToken) return null;

  const updatedConfig = applyCSRFToken(error.config, newCSRFToken);
  return retryRequest(updatedConfig);
}

/**
 * Creates fetch request options with CSRF token
 * @param {string} method - HTTP method
 * @param {object} [data] - Optional data to send
 * @param {AbortSignal} [signal] - Optional AbortSignal
 * @returns {RequestInit} Fetch request options
 */
export function createFetchOptions(
  method: string,
  data?: object,
  signal?: AbortSignal,
): RequestInit {
  const csrfToken = getCSRFToken();

  const options: RequestInit = {
    method,
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken || "",
    },
    signal,
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  return options;
}

/**
 * Handles CSRF errors in fetch responses
 * @param {Response} response - The fetch response
 * @param {string} url - The URL to request
 * @param {object} data - The original request data
 * @param {AbortSignal} [signal] - Optional AbortSignal
 * @returns {Promise<Response | null>} A new response or null if refresh failed
 */
export async function handleFetchCSRFError(
  response: Response,
  url: string,
  data: object,
  signal?: AbortSignal,
): Promise<Response | null> {
  try {
    if (!(response.type === "cors" && response.status === 403)) return null;

    await refreshCSRFToken();

    const newCSRFToken = getCSRFToken();
    if (!newCSRFToken) {
      return null;
    }

    // Create new request with refreshed token
    const newResponse = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": newCSRFToken,
      },
      body: JSON.stringify(data),
      signal,
    });

    return newResponse;
  } catch (error) {
    console.error("Error handling fetch CSRF error:", error);
    return null;
  }
}

/**
 * Encodes URI components safely, handling special characters correctly
 * @param value - The string to encode
 * @returns Properly encoded URI component
 */
export function safeEncodeURIComponent(value: string): string {
  let encoded = encodeURIComponent(value);

  encoded = encoded.replace(/%20/g, "+").replace(/#/g, "%23");

  return encoded;
}

/**
 * Creates a safe URL by properly encoding query parameters
 * @param baseUrl - The base URL without query parameters
 * @param params - Object containing query parameters
 * @returns A properly encoded URL
 */
export function createSafeUrl(
  baseUrl: string,
  params: Record<string, string>,
): string {
  const queryParams = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${safeEncodeURIComponent(value)}`,
    )
    .join("&");

  return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
}

/**
 * Function that cleans and formats a numeric value ensuring:
 * - The minus sign can only be at the beginning
 * - There can only be one decimal point
 * - If a minus sign is added at the end of a positive number, it is moved to the beginning
 *
 * @param value The value to clean and format
 * @param allowZero Whether to allow zero values (default: true)
 * @param allowNegative Whether to allow negative values (default: true)
 * @returns The cleaned and formatted value
 */
export function formatNumericInput(
  value: string,
  allowZero: boolean = true,
  allowNegative: boolean = true,
  allowDecimal: boolean = true,
): string {
  const endsWithMinus = value.endsWith("-");

  let cleanedValue = value.replace(allowDecimal ? /[^-0-9.]/g : /[^-0-9]/g, "");

  // Handle negative numbers only if allowed
  if (allowNegative) {
    if (
      endsWithMinus &&
      !cleanedValue.startsWith("-") &&
      cleanedValue.length > 0
    ) {
      cleanedValue = "-" + cleanedValue.replace(/-/g, "");
    } else if (cleanedValue.includes("-") && cleanedValue.indexOf("-") > 0) {
      cleanedValue = cleanedValue.replace(/-/g, "");
    } else if (
      cleanedValue.indexOf("-") === 0 &&
      cleanedValue.lastIndexOf("-") > 0
    ) {
      cleanedValue = "-" + cleanedValue.substring(1).replace(/-/g, "");
    }
  } else {
    // Remove all minus signs if negative numbers are not allowed
    cleanedValue = cleanedValue.replace(/-/g, "");
  }

  if (allowDecimal) {
    const firstDotIndex = cleanedValue.indexOf(".");
    if (firstDotIndex !== -1 && cleanedValue.lastIndexOf(".") > firstDotIndex) {
      cleanedValue =
        cleanedValue.substring(0, firstDotIndex + 1) +
        cleanedValue.substring(firstDotIndex + 1).replace(/\./g, "");
    }
  } else {
    cleanedValue = cleanedValue.replace(/\./g, "");
  }

  // Handle zero restriction
  if (
    !allowZero &&
    (cleanedValue === "0" ||
      cleanedValue === "0." ||
      cleanedValue === "-0" ||
      cleanedValue === "-0.")
  ) {
    return "";
  }

  return cleanedValue;
}
